import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import {
  DRONE_NDVI_RANGE,
  DRONE_VEGETATION_CLASSES,
  ndviToEstimatedYieldTonsHa,
  type VegetationClassDef,
} from '../constants/dronePrescriptionLayers';

/** Downsample high-res drone COGs for TiTiler POST /statistics (avoids 30s timeout). */
const TITILER_STATS_MAX_SIZE = 512;

/** Drone NDVI COGs are uint8 RGB (0–255); stats run on band 1 (red channel of false-colour NDVI). */
const DRONE_COG_STATS_RANGE: [number, number] = [0, 255];

const HISTOGRAM_BINS = 40;

/** Bins starting at or above this byte value are white outside-AOI padding. */
const BACKGROUND_BYTE_EDGE_MIN = 247;

export interface VegetationClassResult extends VegetationClassDef {
  zonalMean: number;
  areaHa: number;
  areaPercent: number;
  estYieldTonsHa: number;
  productionTons: number;
  pixelCount: number;
}

/** @deprecated Use VegetationClassResult */
export type PrescriptionClassResult = VegetationClassResult;

export interface HistogramBin {
  value: number;
  count: number;
}

export interface DronePrescriptionAnalytics {
  indexName: string;
  classes: PrescriptionClassResult[];
  histogram: HistogramBin[];
  totalAreaHa: number;
  totalProductionTons: number;
  overallMeanYieldTonsHa: number;
  overallMean: number;
  validPercent: number;
  validPixelCount: number;
}

export type DronePrescriptionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; data: DronePrescriptionAnalytics }
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

function byteToNdvi(byte: number): number {
  const [ndviMin, ndviMax] = DRONE_NDVI_RANGE;
  const [byteMin, byteMax] = DRONE_COG_STATS_RANGE;
  return ((byte - byteMin) / (byteMax - byteMin)) * (ndviMax - ndviMin) + ndviMin;
}

function geoJsonAreaHa(geometry: GeoJSON.Geometry): number {
  const rings: number[][][] = [];
  if (geometry.type === 'Polygon') {
    rings.push(geometry.coordinates[0]);
  } else if (geometry.type === 'MultiPolygon') {
    for (const poly of geometry.coordinates) {
      rings.push(poly[0]);
    }
  } else {
    return 0;
  }

  let areaM2 = 0;
  for (const ring of rings) {
    if (ring.length < 3) continue;
    const latRad =
      ring.reduce((sum, c) => sum + (c[1] * Math.PI) / 180, 0) / ring.length;
    const mPerDegLat = 111_320;
    const mPerDegLon = 111_320 * Math.cos(latRad);
    let sum = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      sum += (x2 - x1) * mPerDegLon * (y2 + y1) * mPerDegLat * 0.5;
    }
    areaM2 += Math.abs(sum);
  }
  return areaM2 / 10_000;
}

function classForNdviValue(value: number, classes: VegetationClassDef[]): VegetationClassDef | null {
  for (let i = 0; i < classes.length; i++) {
    const cls = classes[i];
    const isLast = i === classes.length - 1;
    if (value >= cls.min && (isLast ? value <= cls.max : value < cls.max)) {
      return cls;
    }
  }
  return null;
}

/** TiTiler returns histogram as [counts[], bin_edges[]]. */
export function parseTitilerHistogram(raw: unknown): HistogramBin[] {
  if (!Array.isArray(raw) || raw.length < 2) return [];
  const counts = raw[0];
  const edges = raw[1];
  if (!Array.isArray(counts) || !Array.isArray(edges)) return [];

  const bins: HistogramBin[] = [];
  for (let i = 0; i < counts.length; i++) {
    const count = Number(counts[i]);
    const edgeMin = Number(edges[i]);
    const edgeMax = Number(edges[i + 1]);
    if (Number.isNaN(count) || count <= 0 || Number.isNaN(edgeMin) || Number.isNaN(edgeMax)) {
      continue;
    }
    if (edgeMin >= BACKGROUND_BYTE_EDGE_MIN) continue;
    const center = (edgeMin + edgeMax) / 2;
    bins.push({ value: center, count });
  }
  return bins;
}

function buildClassResults(
  histogram: HistogramBin[],
  totalValidPixels: number,
  totalAreaHa: number
): VegetationClassResult[] {
  const accum = new Map<
    string,
    { sum: number; count: number; def: VegetationClassDef }
  >();

  for (const cls of DRONE_VEGETATION_CLASSES) {
    accum.set(cls.id, { sum: 0, count: 0, def: cls });
  }

  for (const bin of histogram) {
    const ndvi = byteToNdvi(bin.value);
    const cls = classForNdviValue(ndvi, DRONE_VEGETATION_CLASSES);
    if (!cls) continue;
    const row = accum.get(cls.id)!;
    row.sum += ndvi * bin.count;
    row.count += bin.count;
  }

  return DRONE_VEGETATION_CLASSES.map((def) => {
    const row = accum.get(def.id)!;
    const pixelCount = row.count;
    const areaHa =
      totalValidPixels > 0 ? totalAreaHa * (pixelCount / totalValidPixels) : 0;
    const areaPercent = totalValidPixels > 0 ? (pixelCount / totalValidPixels) * 100 : 0;
    const zonalMean = pixelCount > 0 ? row.sum / pixelCount : 0;
    const estYieldTonsHa = ndviToEstimatedYieldTonsHa(zonalMean);
    const productionTons = areaHa * estYieldTonsHa;
    return {
      ...def,
      zonalMean,
      areaHa,
      areaPercent,
      estYieldTonsHa,
      productionTons,
      pixelCount,
    };
  });
}

/** Build analytics from a TiTiler POST /statistics FeatureCollection (or single feature props). */
export function buildDroneAnalyticsFromTitilerResponse(
  geometry: GeoJSON.Geometry,
  titilerJson: Record<string, unknown>
): DronePrescriptionAnalytics | null {
  const feat = (titilerJson.features as Record<string, unknown>[] | undefined)?.[0];
  const featProps = (feat?.properties ?? titilerJson.properties) as Record<string, unknown> | undefined;
  const statsRoot = featProps?.statistics as Record<string, unknown> | undefined;
  const stats = (statsRoot?.b1 ?? statsRoot?.['1']) as Record<string, unknown> | undefined;
  if (!stats) return null;

  const histogramRaw = parseTitilerHistogram(stats.histogram);
  const histogramPixelCount = histogramRaw.reduce((sum, b) => sum + b.count, 0);
  const totalStatsCount = Number(stats.count ?? 0);
  const validPixelCount =
    histogramPixelCount > 0 ? histogramPixelCount : totalStatsCount;
  const validPercent = Number(stats.valid_percent ?? 0);

  if (validPixelCount <= 0) return null;

  const geometryAreaHa = geoJsonAreaHa(geometry);
  const totalAreaHa =
    totalStatsCount > 0
      ? geometryAreaHa * (validPixelCount / totalStatsCount)
      : geometryAreaHa;

  const classes = buildClassResults(histogramRaw, validPixelCount, totalAreaHa);
  const totalProductionTons = classes.reduce((sum, c) => sum + c.productionTons, 0);

  const rawMean = Number(stats.mean ?? 0);
  const overallMean =
    rawMean >= BACKGROUND_BYTE_EDGE_MIN ? 0 : byteToNdvi(rawMean);
  const overallMeanYieldTonsHa = ndviToEstimatedYieldTonsHa(overallMean);

  return {
    indexName: 'NDVI',
    classes,
    histogram: histogramRaw.map((b) => ({ value: byteToNdvi(b.value), count: b.count })),
    totalAreaHa,
    totalProductionTons,
    overallMeanYieldTonsHa,
    overallMean,
    validPercent,
    validPixelCount,
  };
}

export async function fetchDronePrescriptionAnalytics(
  cogUrl: string,
  geometry: GeoJSON.Geometry,
  signal?: AbortSignal
): Promise<DronePrescriptionAnalytics | null> {
  const [rangeMin, rangeMax] = DRONE_COG_STATS_RANGE;
  const params = new URLSearchParams({
    url: cogUrl,
    bidx: '1',
    max_size: String(TITILER_STATS_MAX_SIZE),
    histogram_bins: String(HISTOGRAM_BINS),
    histogram_range: `${rangeMin},${rangeMax}`,
  });

  const featureBody = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  };

  const res = await fetch(`${DIGISAKA_API_ORIGIN}/api/statistics?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(featureBody),
    signal,
  });

  if (!res.ok) {
    throw new Error('Could not analyse vegetation for this area. Please try again.');
  }

  const data = (await res.json()) as Record<string, unknown>;
  return buildDroneAnalyticsFromTitilerResponse(geometry, data);
}
